import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../store/utility';

// Define a service using a base URL and expected endpoints
export const alarmsAPI = createApi({
    reducerPath: 'alarmsAPI',
    // Base is /api/alarms/ (not .../alarmV2/) so sibling endpoints like feature_table/ fit;
    // the alarmV2 endpoints keep the same final URL via the 'alarmV2/...' prefix.
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/alarms/' }),
    tagTypes: ['Alarm', 'AlarmConfiguration', 'AlarmEvent', 'VariableTable'],
    endpoints: (builder) => ({
        getAlarmsReport: builder.query({
            query: (params) => ({
                url: 'alarmV2/report/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result, error, arg) =>
                result && arg && arg.node ? [{ type: 'AlarmConfiguration', id: arg.node }] : ['AlarmConfiguration'],
        }),
        configureAlarms: builder.mutation({
            query: (params) => ({
                url: 'alarmV2/configure/',
                method: 'POST',
                withCredentials: true,
                data: { ...params },
            }),
            // invalidatesTags: (result) => ['AlarmConfiguration'],
        }),
        getAlarms: builder.query({
            query: (params) => ({
                url: 'alarmV2/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result) => {
                return result ? [...result.map((alarm) => ({ type: 'Alarm', id: alarm.id })), 'Alarm'] : ['Alarm'];
            },
        }),
        // Relational alarm model (v2) writes: identity + versioned threshold config.
        // provision: { node_id, feature, armed, name?, component_path?, threshold } -> { id }
        provisionAlarm: builder.mutation({
            query: (data) => ({
                url: 'alarmV2/provision/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            invalidatesTags: ['Alarm'],
        }),
        // reconfigure: { threshold } appends a new (current) threshold config version.
        reconfigureAlarm: builder.mutation({
            query: ({ id, threshold }) => ({
                url: `alarmV2/${id}/reconfigure/`,
                method: 'POST',
                withCredentials: true,
                data: { threshold },
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Alarm', id: arg.id }, 'Alarm'],
        }),
        // remove: soft delete (disarm + tombstone).
        removeAlarm: builder.mutation({
            query: (id) => ({
                url: `alarmV2/${id}/remove/`,
                method: 'POST',
                withCredentials: true,
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Alarm', id }, 'Alarm'],
        }),
        // Online variable table for a machine: every wired variable of every online
        // measurement point with its latest value, limit and condition — including
        // variables with no alarm and variables with no data, so the drawer can draw a
        // complete per-point grid. Distinct from the feature table, whose row is one
        // armed alarm and whose values are anchored on a trigger.
        getVariableTable: builder.query({
            query: ({ machineId, lookbackDays } = {}) => ({
                url: 'variable_table/',
                method: 'GET',
                withCredentials: true,
                params: {
                    parent: machineId,
                    ...(lookbackDays ? { lookback_days: lookbackDays } : {}),
                },
            }),
            transformResponse: (response) => response?.data ?? [],
            providesTags: (result, error, arg) => [{ type: 'VariableTable', id: arg?.machineId }],
        }),
    }),
});

export const {
    useGetAlarmsReportQuery,
    useConfigureAlarmsMutation,
    useGetAlarmsQuery,
    useGetVariableTableQuery,
    useProvisionAlarmMutation,
    useReconfigureAlarmMutation,
    useRemoveAlarmMutation,
} = alarmsAPI;
export const alarmsAPIReducer = alarmsAPI.reducer;
export const alarmsAPIReducerPath = alarmsAPI.reducerPath;
export const alarmsAPIReducerMiddleware = alarmsAPI.middleware;
